import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { IAppState } from 'core/store/store'
import styles from '../update-services/UpdateServices.module.scss'
import { Translate } from 'react-localize-redux'
import {
  Button,
  ButtonType,
  Form,
  FormGroup,
  Input,
  IToastNotification,
  SubmitButton,
  ToasterContainer,
  ToasterMessageType,
  Upload,
  UploadSizeTypes
} from '@digicert/dcone-common-ui'
import { translate } from 'shared/helpers/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-light-svg-icons'
import { LIMIT_MAX, uploadFileOptions } from 'core/utils/constants'
import { createBulkUsers } from 'secure-pages/profile/upload-cert/UploadCert.actions'
import { fetchClientAuthCerts } from 'secure-pages/client-auth/ClientAuth.actions'
import { ISort } from 'core/actions/Actions.interfaces'

export const UploadClientAuthCertificate = (
  { onCancel, createBulkUsers, user, certificates, setCertificates, fetchClientAuthCerts }) => {

  const [name, setName] = useState<string>('')
  const [nameMaxLengthError, setNameMaxLengthError] = useState(false)
  const [nameUniqueError, setNameUniqueError] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [errorState, setErrorState] = useState(false)
  const [disableNext, setDisableNext] = useState(true)
  const [uploadList, setUploadList] = useState<any>(false);
  const [cert, setCert] = useState<any>()
  const [notifications, setNotifications] = useState<IToastNotification[]>([])

  const params: ISort = { limit: LIMIT_MAX, offset: 0 }
  params.sort = [
    {
      columnKey: `name:asc`
    }
  ]

  const defaultUploadList = {
    showPreviewIcon: true,
    extra: () => { },
    showDownloadIcon: false,
    showRemoveIcon: true,
    downloadIcon: false,
    removeIcon: (
      <FontAwesomeIcon
        size='1x'
        className={styles.deleteIcon}
        icon={faTrash}
      />
    )
  };


  useEffect(() => {
    setName('')
    setErrorMsg('')
    setErrorState(false)
    setNameMaxLengthError(false)
    setNameUniqueError(false)
    setUploadList(false)
    setDisableNext(true)
    setCert(undefined)
  }, [onCancel])

  const handleCancel = (e) => {
    e.preventDefault()
    if (onCancel) {
      onCancel(e)
    }
  }

  const onSubmit = (e) => {
    e.preventDefault()
    if (cert && name) {
      const data = new FormData()
      data.append('x509Cert', cert)
      const payload = {
        name: name
      }
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      data.append('request', blob)
      const options = {
        data,
        config: {
          headers: { 'content-type': 'multipart/form-data' }
        },
        id: user.id,
        type: 'standard'
      }
      createBulkUsers(options).then((res: any) => {
        if (res.status === 201) {
          setNotifications(
            [...notifications,
            {
              type: ToasterMessageType.SUCCESS,
              description: translate('notifications.certificates.certificateUploadSuccess'),
              id: 'upload-success'
            }
            ]);
          setTimeout(() => {
            fetchClientAuthCerts(params, user?.id).then(
              (response: any) => {
                if (response) {
                  setCertificates(response.items)
                }
              }
            )
            handleCancel(e)
          }, 1000)
  
        } else {
          setNotifications(
            [...notifications,
            {
              type: ToasterMessageType.ERROR,
              description: translate(res.data.error.message),
              id: 'upload-failed'
            }
            ]);
        }
        setTimeout(() => {
          setNotifications([])
        }, 2000)
      })
    }
  }

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    if (cert) {
      setDisableNext(false)
    }
    if (
      certificates &&
      certificates.find(
        (cert) => cert.name.toLowerCase().trim() === event.target.value.toLowerCase().trim() && cert.user_id === user.id
      )
    ) {
      setNameUniqueError(true)
    } else {
      setNameUniqueError(false)
    }

    if (event.target.value.length > 255) {
      setNameMaxLengthError(true)
    } else {
      setNameMaxLengthError(false)
    }
  }

  const validateName = useCallback(() => {
    if (nameUniqueError) {
      return translate('common.form.validation.clientAuthCertUnique') as string
    }

    if (nameMaxLengthError) {
      return translate('notifications.errors.maxLengthError') as string
    }
  }, [nameUniqueError, nameMaxLengthError])



  const onFileUploadChange = (info) => {
    if (info.file.status === 'done') {
      setCert(info.file.originFileObj)
      if (name !== '' && !errorState && !nameMaxLengthError && !nameUniqueError) {
        setDisableNext(false)
      }
    }
  }

  const onBeforeFileUpload = (file) => {
    const sizeRounded = Math.round(file.size / 4096 * 100) / 100
    const emptyFile = file.size === 0
    const fileLesserThan4KB = sizeRounded > uploadFileOptions.FILE_SIZE_LIMIT
    setErrorState(false)
    if (emptyFile) {
      setErrorMsg(translate('common.form.validation.emptyFile') as string)
      setErrorState(true)
      setDisableNext(true)
      setUploadList(false)
    }
    if (fileLesserThan4KB) {
      setErrorMsg(translate('common.form.validation.largeFileSize') as string)
      setErrorState(true)
      setDisableNext(true)
      setUploadList(false)
    }
    if (!emptyFile && !fileLesserThan4KB) {
      setUploadList(defaultUploadList)
      setErrorState(false)
      setErrorMsg('')
    }
    const error = emptyFile && fileLesserThan4KB
    return !error
  }

  const resetError = () => {
    setErrorMsg('')
    setErrorState(false)
  }

  const onFileUploadRequest = async options => {
    const { onSuccess } = options;
    setTimeout(() => {
      onSuccess('ok')
    }, 0)
  }

  const onFileRemove = useCallback(
    () => {
      resetError()
      setDisableNext(true)
    },
    [setDisableNext]
  );
  const hideNotification = (id: string | number) => {
    setNotifications(notifications.filter((notification) => notification.id !== id));
  }


  return (
    <>
      <div className={styles.editServices}>
        <Form center={false} onSubmit={(e) => onSubmit(e)}>

          <FormGroup>
            <Input
              name='certificate_name'
              label={<Translate id='common.form.name' />}
              required
              value={name}
              pattern='^[\w \-]*$'
              errors={{
                valueMissing: translate('common.form.validation.required'),
                patternMismatch: translate('notifications.errors.invalidFriendlyNameError')
              }}
              className={styles.apiName}
              onChange={onNameChange}
              validate={validateName}
            />
          </FormGroup>
          <Upload
            errorMessage={errorMsg}
            beforeUpload={onBeforeFileUpload}
            label={<Translate id='uploadCert.uploadInfoLabel' />}
            help={<Translate id='uploadCert.uploadInfoSupportedFileFormats' />}
            dragDropMessage={<Translate id='bulkUserUpload.uploadInstructions' />}
            listType='text'
            uploadType={'dragNdrop'}
            multiple={false}
            maxCount={1}
            uploadSize={UploadSizeTypes.LARGE}
            showUploadList={uploadList}
            onChange={onFileUploadChange}
            customRequest={onFileUploadRequest}
            onRemove={onFileRemove}
          />

          <div className={styles.formActions}>
            <Button buttonType={ButtonType.SECONDARY} onClick={handleCancel}>
              <Translate id='common.form.buttons.cancelBtn' />
            </Button>
            <SubmitButton disabled={disableNext}>
              <Translate id='clientAuth.uploadBtn' />
            </SubmitButton>
          </div>

        </Form>
        <ToasterContainer hideNotification={hideNotification} notifications={notifications} />
      </div>
    </>
  )
}

export default connect(
  (state: IAppState) => {
    return {
    }
  },
  {
    createBulkUsers,
    fetchClientAuthCerts
  }
)(UploadClientAuthCertificate)
